/**
 * What Ceetrix is and the command that connects the coding agent
 * Story 586: Web sign-up tells new users to connect their coding agent
 *
 * Shared by the connect step window and the empty Projects panel.
 */

import { useCallback, useState } from 'react';
import './ConnectAgentModal.css';

const CONNECT_COMMAND = 'npx ceetrix';

type CopyState = 'idle' | 'copied' | 'failed';

const COPY_LABELS: Record<CopyState, string> = {
  idle: 'Copy',
  copied: 'Copied',
  failed: 'Copy failed',
};

interface ConnectAgentInstructionsProps {
  /** Line shown between the statement and the command */
  lead?: string;
}

export function ConnectAgentInstructions({ lead }: ConnectAgentInstructionsProps) {
  const [copyState, setCopyState] = useState<CopyState>('idle');

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(CONNECT_COMMAND);
      setCopyState('copied');
    } catch {
      // No clipboard API, or the browser refused: the hint tells the person to copy by hand
      setCopyState('failed');
    }
  }, []);

  return (
    <div className="connect-instructions">
      <p className="connect-instructions__what">
        Ceetrix is an MCP server. Your coding agent talks to it while you work.
      </p>
      {lead && <p className="connect-instructions__lead">{lead}</p>}
      <div className="connect-instructions__command-row">
        <code className="connect-instructions__command" data-testid="connect-command">
          {CONNECT_COMMAND}
        </code>
        <button
          type="button"
          className="connect-instructions__copy"
          onClick={handleCopy}
          data-testid="copy-command-button"
        >
          {COPY_LABELS[copyState]}
        </button>
      </div>
      {copyState === 'failed' && (
        <p className="connect-instructions__hint" data-testid="copy-failed-hint">
          Select the command and copy it yourself.
        </p>
      )}
    </div>
  );
}
